import React from 'react'
import { Box, FormControl, IconButton, InputBase, type InputBaseProps, styled } from '@mui/material'
import { type SxProps, useTheme } from '@mui/material/styles'

import { FormHelperText } from '~components/common'
import { Icon } from '~components/internal'

interface NumberInputProps extends Omit<InputBaseProps, 'onChange'> {
  placeholder?: string
  label?: string
  helperText?: string
  errorText?: string
  value?: number | null
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  inputStyle?: SxProps
  min?: number
  max?: number
  step?: number
  id?: string
}

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  margin: theme.spacing(0, 0),
}))

const NumberInputBase = styled(InputBase)(({ theme, error, disabled }) => ({
  width: '100%',
  // Apply boxShadow styling to the root container
  borderRadius: 4,
  backgroundColor: disabled ? theme.palette.action.disabledInput : 'white',
  boxShadow: disabled
    ? `inset 0 0 0 1px ${theme.palette.text.disabled}`
    : error
      ? `inset 0 0 0 2px ${theme.palette.error.main}`
      : `inset 0 0 0 1px ${theme.palette.input.default}`,
  transition: theme.transitions.create(['box-shadow']),

  '&:hover': !disabled && {
    boxShadow: error
      ? `inset 0 0 0 2px ${theme.palette.error.main}`
      : `inset 0 0 0 2px ${theme.palette.input.hover}`,
  },

  '&:focus-within': !disabled && {
    boxShadow: `inset 0 0 0 2px ${theme.palette.input.hover}, 0 0 0 2px ${theme.palette.input.focus}`,
  },
  // Only apply flexbox when adornments are present
  '&.MuiInputBase-adornedStart, &.MuiInputBase-adornedEnd': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiInputBase-input': {
    fontSize: 16,
    padding: '8.5px 12px',
    paddingLeft: '16px', // Increased left padding for better spacing
    fontFamily: theme.typography.body2.fontFamily,
    border: 'none',
    backgroundColor: 'transparent',
    outline: 'none',
    width: '100%',
    '&:focus': {
      outline: 'none',
    },
  },
  // Style the adornments to be inside the border
  '& .MuiInputAdornment-root': {
    color: theme.palette.text.secondary,
    maxHeight: 'none',
  },
  '& .MuiInputAdornment-positionStart': {
    marginLeft: '16px', // Increased left margin for adornment
    marginRight: '12px', // Increased right margin for better spacing
  },
  '& .MuiInputAdornment-positionEnd': {
    marginLeft: '12px', // Increased left margin for better spacing
    marginRight: '8px', // Reduced right margin to allow easier override
  },
  // When there's a start adornment, adjust the input
  '&.MuiInputBase-adornedStart .MuiInputBase-input': {
    paddingLeft: '4px',
    flex: 1,
    minWidth: 0,
  },
  // When there's an end adornment, adjust the input
  '&.MuiInputBase-adornedEnd .MuiInputBase-input': {
    paddingRight: '4px',
    flex: 1,
    minWidth: 0,
  },
}))

const ChevronButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  position: 'absolute',
  right: 2,
  width: 28,
  height: 20,
  minWidth: 28,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-disabled': {
    color: theme.palette.action.disabled,
  },
}))

const NumberInput: React.FC<NumberInputProps> = ({
  label,
  placeholder = '',
  helperText,
  errorText,
  onChange,
  inputStyle,
  min = 0,
  max = Infinity,
  step = 1,
  value = null,
  disabled,
  id,
  ...props
}) => {
  // Create a ref to the input element
  const inputRef = React.useRef<HTMLInputElement>(null)
  const inputId = id || React.useMemo(() => `number-input-${label?.replace(' ', '-')}`, [label])
  const theme = useTheme()

  // Prevent mouse wheel from changing number input value
  const wheelHandler = { onWheel: (e: React.WheelEvent): void => (e.target as HTMLElement).blur() }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(event)
  }

  const getCurrentValue = (): number | null => {
    // @ts-ignore
    if (value === null || value === undefined || value === '') return null
    if (typeof value === 'number') return value
    if (typeof value === 'string' && value !== '') {
      const num = parseFloat(value)
      return isNaN(num) ? null : num
    }
    return null
  }

  const createSyntheticEvent = (newValue: number | null): React.ChangeEvent<HTMLInputElement> => {
    if (inputRef.current) {
      inputRef.current.value = newValue === null ? '' : newValue.toString()
      const event = new Event('change', { bubbles: true })
      Object.defineProperty(event, 'target', { value: inputRef.current })
      return event as unknown as React.ChangeEvent<HTMLInputElement>
    }
    // Fallback if ref is not available
    return {
      target: { value: newValue === null ? '' : newValue.toString() },
      currentTarget: { value: newValue === null ? '' : newValue.toString() },
    } as React.ChangeEvent<HTMLInputElement>
  }

  const increment = (): void => {
    const currentValue = getCurrentValue()
    const newValue = Math.min((currentValue ?? 0) + step, max)
    onChange(createSyntheticEvent(newValue))
  }

  const decrement = (): void => {
    const currentValue = getCurrentValue()
    const newValue = Math.max((currentValue ?? 0) - step, min)
    onChange(createSyntheticEvent(newValue))
  }

  const isAtMax = (getCurrentValue() ?? 0) >= max
  const isAtMin = (getCurrentValue() ?? 0) <= min
  return (
    <StyledFormControl error={!!errorText} sx={inputStyle}>
      <Box sx={{ position: 'relative' }}>
        <NumberInputBase
          id={inputId}
          inputRef={inputRef}
          type='number'
          placeholder={placeholder}
          error={!!errorText}
          onChange={handleChange}
          value={value === null ? '' : value}
          disabled={disabled}
          inputProps={{
            min,
            max,
            step,
            id: inputId,
            'aria-labelledby': label ? inputId : undefined,
            'aria-describedby': errorText || helperText ? `${inputId}-helper-text` : undefined,
            '&:hover': {
              borderColor: theme.palette.primary.main,
            },
          }}
          {...wheelHandler}
          {...props}
        />
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 32,
            borderLeft: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <ChevronButton
            aria-label='Increase number'
            onClick={increment}
            disabled={disabled || isAtMax}
            size='small'
            sx={{ top: 0 }}
            role='button'
            aria-roledescription='stepper'
          >
            <Icon name='chevronUp' fontSize='small' />
          </ChevronButton>
          <ChevronButton
            aria-label='Decrease number'
            onClick={decrement}
            disabled={disabled || isAtMin}
            size='small'
            sx={{ bottom: 0 }}
            role='button'
            aria-roledescription='stepper'
          >
            <Icon name='chevronDown' fontSize='small' />
          </ChevronButton>
        </Box>
      </Box>
      {(errorText || helperText) && (
        <FormHelperText id={`${inputId}-helper-text`} error={!!errorText}>
          {errorText || helperText}
        </FormHelperText>
      )}
    </StyledFormControl>
  )
}

export default NumberInput
